/**
 * MatchingPage.js — Premium Marriage Matching (12 Poruthams)
 * 
 * Full DOB/TOB/POB based horoscope matching with:
 *  - Boy & Girl tabs with Name, Date of Birth, Time of Birth, Place of Birth
 *  - Location search with auto-complete (lat/lng)
 *  - Confirmation screen before calculation
 *  - Results with 12 Porutham score, Dosha Samya, Dasa Sandhi
 *  - Print/Save PDF, New Match actions
 * 
 * Ported from: d:\ionic\paranthaman5.0\src\app\pages\matching\matching.page.ts
 */

import { useState, useEffect } from 'react';
import { VakyaHoroscopeMatchingService } from '../services/vakya-horoscope-matching';

const T = {
  bg: '#faf8f5', cream: '#ffffff', greenDk: '#8B0000', maroon: '#8B0000',
  orange: '#e65100', border: '#e0dcd3',
  header: 'linear-gradient(135deg,#8B0000,#4A0000)',
  boyAccent: '#1565c0', girlAccent: '#c62828'
};

const MatchingPage = () => {
  const [activeTab, setActiveTab] = useState('boy');
  const [boyData, setBoyData] = useState({
    name: '', date: '', time: '', place: '',
    latitude: '', longitude: '', timezone: 5.5, language: 'ta'
  });
  const [girlData, setGirlData] = useState({
    name: '', date: '', time: '', place: '',
    latitude: '', longitude: '', timezone: 5.5
  });
  const [locations, setLocations] = useState([]);
  const [boySearch, setBoySearch] = useState('');
  const [girlSearch, setGirlSearch] = useState('');
  const [showBoySuggestions, setShowBoySuggestions] = useState(false);
  const [showGirlSuggestions, setShowGirlSuggestions] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/data/locations.json')
      .then(r => r.json())
      .then(data => setLocations(data))
      .catch(e => console.error("Could not load locations", e));
  }, []);

  const filterLocations = (query) => {
    if (!query || query.length < 2) return [];
    const lq = query.toLowerCase();
    return locations.filter(l =>
      l.name.toLowerCase().includes(lq) ||
      (l.state && l.state.toLowerCase().includes(lq))
    ).slice(0, 8);
  };

  const selectBoyLocation = (loc) => {
    setBoyData({ ...boyData, place: loc.name, latitude: loc.lat, longitude: loc.lng });
    setBoySearch(loc.name);
    setShowBoySuggestions(false);
  };

  const selectGirlLocation = (loc) => {
    setGirlData({ ...girlData, place: loc.name, latitude: loc.lat, longitude: loc.lng });
    setGirlSearch(loc.name);
    setShowGirlSuggestions(false);
  };

  const isBoyValid = boyData.name && boyData.date && boyData.time && boyData.place && boyData.latitude && boyData.longitude;
  const isGirlValid = girlData.name && girlData.date && girlData.time && girlData.place && girlData.latitude && girlData.longitude;
  const isFormValid = isBoyValid && isGirlValid;

  const handleShowConfirmation = () => {
    if (!isFormValid) {
      setError('Please fill all fields for both Boy and Girl before calculating.');
      return;
    }
    setError(null);
    setShowConfirmation(true);
  };

  const calculate = async () => {
    setLoading(true);
    setError(null);
    try {
      const svc = new VakyaHoroscopeMatchingService();
      const boyDetails = {
        name: boyData.name,
        date: boyData.date,
        time: boyData.time,
        place: boyData.place,
        latitude: parseFloat(boyData.latitude),
        longitude: parseFloat(boyData.longitude),
        timezone: parseFloat(boyData.timezone)
      };
      const girlDetails = {
        name: girlData.name,
        date: girlData.date,
        time: girlData.time,
        place: girlData.place,
        latitude: parseFloat(girlData.latitude),
        longitude: parseFloat(girlData.longitude),
        timezone: parseFloat(girlData.timezone)
      };
      const res = await svc.calculateMatching(boyDetails, girlDetails, boyData.language);
      setResult(res);
      setShowConfirmation(false);
    } catch (e) {
      console.error('Matching calculation error:', e);
      setError('Failed to calculate matching. Please check input and try again.');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setBoyData({ name: '', date: '', time: '', place: '', latitude: '', longitude: '', timezone: 5.5, language: 'ta' });
    setGirlData({ name: '', date: '', time: '', place: '', latitude: '', longitude: '', timezone: 5.5 });
    setBoySearch(''); setGirlSearch('');
    setShowConfirmation(false);
    setResult(null);
    setError(null);
  };

  // ── Shared input styles ──
  const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '0.95rem' };
  const labelStyle = (color) => ({ fontSize: '0.85rem', fontWeight: 700, color, display: 'block', marginBottom: '4px' });
  const fieldGroup = { marginBottom: '12px' };

  // ── Render input form for boy or girl ──
  const renderPersonForm = (person) => {
    const isBoy = person === 'boy';
    const data = isBoy ? boyData : girlData;
    const setData = isBoy ? setBoyData : setGirlData;
    const search = isBoy ? boySearch : girlSearch;
    const setSearch = isBoy ? setBoySearch : setGirlSearch;
    const showSugg = isBoy ? showBoySuggestions : showGirlSuggestions;
    const setShowSugg = isBoy ? setShowBoySuggestions : setShowGirlSuggestions;
    const selectLoc = isBoy ? selectBoyLocation : selectGirlLocation;
    const accent = isBoy ? T.boyAccent : T.girlAccent;
    const filtered = filterLocations(search);

    return (
      <div style={{ background: T.cream, padding: '15px', borderRadius: '8px', border: `2px solid ${accent}20` }}>
        {/* Name */}
        <div style={fieldGroup}>
          <label style={labelStyle(accent)}>{isBoy ? '🤵 Boy\'s Name / ஆண் பெயர்' : '👰 Girl\'s Name / பெண் பெயர்'}</label>
          <input type="text" value={data.name} onChange={e => setData({ ...data, name: e.target.value })}
            placeholder={isBoy ? 'Enter Boy\'s Name' : 'Enter Girl\'s Name'} style={inputStyle} />
        </div>

        {/* Language (only for boy tab) */}
        {isBoy && (
          <div style={fieldGroup}>
            <label style={labelStyle(accent)}>Output Language</label>
            <select value={data.language} onChange={e => setData({ ...data, language: e.target.value })}
              style={{ ...inputStyle, fontWeight: 700 }}>
              <option value="ta">Tamil (தமிழ்)</option>
              <option value="en">English</option>
            </select>
          </div>
        )}

        {/* DOB & TOB */}
        <div style={{ ...fieldGroup, display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle(accent)}>Date of Birth / பிறந்த தேதி</label>
            <input type="date" value={data.date} onChange={e => setData({ ...data, date: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle(accent)}>Time of Birth / பிறந்த நேரம்</label>
            <input type="time" value={data.time} onChange={e => setData({ ...data, time: e.target.value })} style={inputStyle} />
          </div>
        </div>

        {/* Place of Birth */}
        <div style={{ ...fieldGroup, position: 'relative' }}>
          <label style={labelStyle(accent)}>Place of Birth / பிறந்த இடம்</label>
          <input type="text" value={search}
            onChange={e => { setSearch(e.target.value); if (e.target.value.length >= 2) setShowSugg(true); else setShowSugg(false); }}
            onFocus={() => search.length >= 2 && setShowSugg(true)}
            placeholder="Type to search location..." style={inputStyle} />
          {showSugg && filtered.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
              border: '1px solid #ccc', zIndex: 10, maxHeight: '200px', overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '0 0 6px 6px'
            }}>
              {filtered.map((l, i) => (
                <div key={i} onClick={() => selectLoc(l)}
                  style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  <div style={{ fontWeight: 600, color: accent, fontSize: '0.9rem' }}>{l.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>{l.state}, {l.country} ({l.lat}, {l.lng})</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lat/Lng/Timezone */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: accent, display: 'block', marginBottom: '4px' }}>Latitude</label>
            <input type="number" step="0.01" value={data.latitude}
              onChange={e => setData({ ...data, latitude: e.target.value })}
              style={{ ...inputStyle, padding: '8px', background: '#f9f9f9' }} readOnly />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: accent, display: 'block', marginBottom: '4px' }}>Longitude</label>
            <input type="number" step="0.01" value={data.longitude}
              onChange={e => setData({ ...data, longitude: e.target.value })}
              style={{ ...inputStyle, padding: '8px', background: '#f9f9f9' }} readOnly />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: accent, display: 'block', marginBottom: '4px' }}>Timezone</label>
            <input type="number" step="0.5" value={data.timezone}
              onChange={e => setData({ ...data, timezone: e.target.value })}
              style={{ ...inputStyle, padding: '8px' }} />
          </div>
        </div>
      </div>
    );
  };

  // ── RESULTS VIEW ──
  if (result) {
    return (
      <div style={{ background: T.bg, minHeight: '80vh', padding: '10px' }}>
        <div style={{ background: T.header, padding: '12px', borderRadius: '10px', textAlign: 'center', marginBottom: '10px' }}>
          <div style={{ color: '#fff', margin: 0, fontSize: '1.2rem', fontWeight: 900, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            💍 ஜாதகப் பொருத்தம் — Matching Results
          </div>
          <div style={{ color: '#ffe0b2', fontSize: '0.85rem' }}>
            {result.boyInfo?.name} ❤️ {result.girlInfo?.name} — Score: {result.totalScore}/12
          </div>
        </div>

        <div style={{ background: T.cream, padding: '15px', borderRadius: '10px', border: '2px solid ' + T.greenDk }}>
          <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #ccc', overflow: 'hidden', height: '1000px' }}>
            <iframe
              id="matching-iframe"
              srcDoc={result.htmlOutput}
              title="Matching PDF Layout"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
            <button onClick={() => {
              const iframe = document.getElementById('matching-iframe');
              if (iframe) { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
            }} style={{
              flex: 1, padding: '12px', border: 'none', background: T.greenDk,
              borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '1rem', fontWeight: 700
            }}>
              🖨️ Print / Save PDF
            </button>
            <button onClick={resetForm} style={{
              flex: 1, padding: '12px', border: '2px solid ' + T.greenDk, background: '#fff',
              borderRadius: '8px', color: T.greenDk, cursor: 'pointer', fontSize: '1rem', fontWeight: 700
            }}>
              🔄 New Match
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CONFIRMATION VIEW ──
  if (showConfirmation) {
    return (
      <div style={{ background: T.bg, minHeight: '80vh', padding: '10px' }}>
        <div style={{ background: T.header, padding: '12px', borderRadius: '10px', textAlign: 'center', marginBottom: '10px' }}>
          <div style={{ color: '#fff', margin: 0, fontSize: '1.2rem', fontWeight: 900, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            📋 Confirm Details / விவரங்களை உறுதிப்படுத்தவும்
          </div>
          <div style={{ color: '#ffe0b2', fontSize: '0.85rem' }}>Please verify the information before matching</div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          {/* Boy Confirmation */}
          <div style={{ flex: '1 1 280px', background: '#e3f2fd', borderRadius: '10px', padding: '15px', border: '2px solid #1565c0' }}>
            <div style={{ fontWeight: 900, fontSize: '1rem', color: '#1565c0', marginBottom: '10px', borderBottom: '2px solid #1565c0', paddingBottom: '5px' }}>
              🤵 Boy's Details
            </div>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Name:</strong> {boyData.name}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>DOB:</strong> {boyData.date}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Time:</strong> {boyData.time}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Place:</strong> {boyData.place}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Timezone:</strong> {boyData.timezone} (UTC)</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Language:</strong> {boyData.language === 'ta' ? 'Tamil' : 'English'}</p>
          </div>

          {/* Girl Confirmation */}
          <div style={{ flex: '1 1 280px', background: '#fce4ec', borderRadius: '10px', padding: '15px', border: '2px solid #c62828' }}>
            <div style={{ fontWeight: 900, fontSize: '1rem', color: '#c62828', marginBottom: '10px', borderBottom: '2px solid #c62828', paddingBottom: '5px' }}>
              👰 Girl's Details
            </div>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Name:</strong> {girlData.name}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>DOB:</strong> {girlData.date}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Time:</strong> {girlData.time}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Place:</strong> {girlData.place}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Timezone:</strong> {girlData.timezone} (UTC)</p>
          </div>
        </div>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '8px', marginBottom: '10px', fontWeight: 600, textAlign: 'center' }}>
            ⚠️ {error}
          </div>
        )}

        <button onClick={calculate} disabled={loading} style={{
          width: '100%', padding: '15px', border: 'none', borderRadius: '8px',
          background: 'linear-gradient(135deg, #c62828, #e65100)', color: '#fff',
          fontSize: '1.1rem', fontWeight: 900, cursor: loading ? 'wait' : 'pointer',
          marginBottom: '10px', boxShadow: '0 4px 12px rgba(198,40,40,0.3)',
          opacity: loading ? 0.7 : 1
        }}>
          {loading ? '⏳ Calculating Marriage Match...' : '💍 CONFIRM & CALCULATE MATCH'}
        </button>

        <button onClick={() => setShowConfirmation(false)} style={{
          width: '100%', padding: '12px', border: '2px solid ' + T.greenDk, background: '#fff',
          borderRadius: '8px', color: T.greenDk, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 700
        }}>
          ← Back to Edit
        </button>
      </div>
    );
  }

  // ── MAIN INPUT FORM ──
  return (
    <div style={{ background: T.bg, minHeight: '80vh', padding: '10px' }}>
      {/* Header */}
      <div style={{ background: T.header, padding: '14px', borderRadius: '10px', textAlign: 'center', marginBottom: '12px' }}>
        <div style={{ color: '#ffffff', margin: 0, fontSize: '1.3rem', fontWeight: 900, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
          💍 ஜாதகப் பொருத்தம்
        </div>
        <div style={{ color: '#ffe0b2', fontSize: '0.85rem' }}>Sri Paranthaman — 12 Porutham Marriage Matching (PDF)</div>
      </div>

      {/* 30-day Free Trial Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #2e7d32, #1b5e20)', color: '#fff',
        padding: '10px 15px', borderRadius: '8px', marginBottom: '12px', textAlign: 'center',
        fontSize: '0.9rem', fontWeight: 700
      }}>
        🎉 Enjoy your 30-day Free Trial! Create and download unlimited PDF horoscope matchings.
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '12px' }}>
        {[['boy', '🤵 Boy\'s Details'], ['girl', '👰 Girl\'s Details']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            flex: 1, border: 'none', padding: '10px', borderRadius: '8px 8px 0 0',
            fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
            background: activeTab === id ? (id === 'boy' ? '#1565c0' : '#c62828') : '#e0e0e0',
            color: activeTab === id ? '#fff' : '#555',
            transition: 'all 0.2s ease'
          }}>
            {label}
            {id === 'boy' && isBoyValid && ' ✅'}
            {id === 'girl' && isGirlValid && ' ✅'}
          </button>
        ))}
      </div>

      {/* Form */}
      {activeTab === 'boy' ? renderPersonForm('boy') : renderPersonForm('girl')}

      {/* Error */}
      {error && (
        <div style={{ background: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '8px', marginTop: '10px', fontWeight: 600, textAlign: 'center' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ marginTop: '15px' }}>
        <button onClick={handleShowConfirmation} disabled={!isFormValid} style={{
          width: '100%', padding: '15px', border: 'none', borderRadius: '8px',
          background: isFormValid ? 'linear-gradient(135deg, #c62828, #e65100)' : '#bdbdbd',
          color: '#fff', fontSize: '1.1rem', fontWeight: 900,
          cursor: isFormValid ? 'pointer' : 'not-allowed',
          boxShadow: isFormValid ? '0 4px 12px rgba(198,40,40,0.3)' : 'none',
          marginBottom: '8px'
        }}>
          💍 CALCULATE MARRIAGE MATCH
        </button>

        <button onClick={resetForm} style={{
          width: '100%', padding: '10px', border: '1px solid #999', background: '#fff',
          borderRadius: '8px', color: '#555', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600
        }}>
          🔄 Reset Forms
        </button>
      </div>

      {/* Validation Hints */}
      {(!isBoyValid || !isGirlValid) && (
        <div style={{ marginTop: '10px', padding: '10px', background: '#fff3e0', borderRadius: '8px', fontSize: '0.8rem', color: '#e65100' }}>
          {!isBoyValid && <div>⚠️ Please complete all Boy's details (Name, DOB, TOB, Place)</div>}
          {!isGirlValid && <div>⚠️ Please complete all Girl's details (Name, DOB, TOB, Place)</div>}
        </div>
      )}
    </div>
  );
};

export default MatchingPage;